import React from "react";
import { Linkedin, ArrowUpRight } from "lucide-react";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_medtech-sales-ai/artifacts/yup5jk0z_WhatsApp%20Image%202026-04-30%20at%204.20.07%20PM.jpeg";

const milestones = [
  {
    org: "Osstem India",
    role: "Sole Sales Manager · South India",
    detail:
      "Sole architect of the South India expansion — built the regional sales network from a blank territory into a thriving revenue hub.",
  },
  {
    org: "Alpha-Bio Tec (Danaher)",
    role: "Strategic Sales Leadership",
    detail:
      "Led complex Med-Tech and digital dentistry deployments across institutional and clinical accounts.",
  },
  {
    org: "Serio Healthcare",
    role: "Founder",
    detail:
      "Pioneered bespoke, end-to-end cutting-edge digital solutions — CAD-CAM, 3D Printing, Thermoforming, and training — for leading dental hospitals.",
  },
  {
    org: "Sebyx",
    role: "Founder & Strategic Architect",
    detail:
      "Fusing 18 years of field-tested intuition with AI-driven proprietary tech to engineer the future of B2B sales.",
  },
];

const sectors = [
  "Defence & Aerospace",
  "Industrial Electronics",
  "Pharma & Chemical",
  "Med-Tech & Dental",
];

export default function FounderBio() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-24 lg:py-32 px-6 md:px-12 lg:px-24 border-t border-sebyx-border bg-sebyx-surface"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Portrait */}
        <div className="lg:col-span-5">
          <div className="relative sebyx-corner border border-sebyx-border">
            <img
              src={PORTRAIT}
              alt="Mandar Prabhudesai — Founder of Sebyx"
              data-testid="founder-portrait"
              className="w-full aspect-[4/5] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sebyx-bg/70 via-sebyx-bg/20 to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-accent">
                  [05] Founder
                </div>
                <div className="font-display text-lg text-sebyx-text mt-1">
                  Mandar Prabhudesai
                </div>
              </div>
              <a
                href="https://www.linkedin.com/in/mandarprabhudesai/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin"
                aria-label="Mandar Prabhudesai on LinkedIn"
                className="w-9 h-9 border border-sebyx-accent text-sebyx-accent flex items-center justify-center hover:bg-sebyx-accent hover:text-sebyx-bg transition-colors"
              >
                <Linkedin size={14} />
              </a>
            </div>
          </div>

          {/* Sector chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {sectors.map((s) => (
              <span
                key={s}
                className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-sebyx-border text-sebyx-muted"
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Bio */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-8 h-px bg-sebyx-accent" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
              [08] Founder Bio
            </span>
          </div>
          <h2
            data-testid="founder-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-sebyx-text leading-[1.05]"
          >
            An operator's{" "}
            <span className="text-sebyx-accent italic font-light">
              18 years
            </span>
            . Not a trainer's theory.
          </h2>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-sebyx-muted mt-4">
            Mandar Prabhudesai · Founder & Strategic Architect
          </p>

          <div
            data-testid="founder-bio-body"
            className="mt-10 space-y-6 font-body text-base md:text-lg leading-relaxed text-sebyx-text/90 max-w-2xl"
          >
            <p>
              Mandar is a specialist in navigating the{" "}
              <span className="text-sebyx-accent">"Last Mile"</span> of Indian
              business — where complex technology meets the reality of the
              ground-up sale. Across nearly two decades, he has established
              himself as a high-authority architect of revenue engines in the
              nation's most rigorous sectors.
            </p>
            <p className="text-sebyx-muted">
              His track record is defined by high-responsibility, high-impact
              growth — from being the lone sales manager building Osstem India's
              South India network from zero, to leading deployments at Alpha-Bio
              Tec (Danaher) and founding Serio Healthcare — pioneering bespoke,
              end-to-end digital solutions (CAD-CAM, 3D Printing, Thermoforming,
              and training) for leading dental hospitals.
            </p>
            <p className="text-sebyx-muted">
              His expertise extends well beyond clinical — covering Defence &
              Aerospace strategic accounts, Industrial Electronics distribution,
              and market entry for Pharma & Chemical process technologies.
            </p>
            <p>
              Today, Mandar drives Sebyx — moving beyond traditional consulting
              by building proprietary AI-driven tech to solve the bottlenecks
              he faced in the field. His mission is singular:{" "}
              <span className="text-sebyx-text">
                fusing 18 years of field-tested intuition with AI-driven
                technologies to engineer the future of sales.
              </span>
            </p>
          </div>

          {/* Milestones */}
          <div
            data-testid="founder-timeline"
            className="mt-14 border-t border-sebyx-border"
          >
            {milestones.map((m, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-4 py-5 border-b border-sebyx-border group hover:bg-sebyx-bg/40 transition-colors -mx-2 px-2"
              >
                <div className="col-span-2 font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-accent pt-1">
                  0{i + 1}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <div className="font-display text-base md:text-lg font-medium text-sebyx-text">
                    {m.org}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted mt-1">
                    {m.role}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 text-sm text-sebyx-muted leading-relaxed">
                  {m.detail}
                </div>
              </div>
            ))}
          </div>

          <a
            href="#contact"
            data-testid="founder-cta"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("sebyx-prefill", {
                  detail: { interest: "BDaaS / Fractional Leadership" },
                })
              )
            }
            className="mt-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-sebyx-accent border-b border-sebyx-accent pb-1 hover:gap-4 transition-all"
          >
            Work with Mandar
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
